import * as React from 'react';
import canCreateTaskInFolder from '../utils/canCreateTaskInFolder';
import DragToEndOfList from './DragToEndOfList';
import EmptySpacer from './EmptySpacer';
import FolderPicker, { FolderPickerStyles } from './Folders/FolderPicker';
import FRE from './FRE';
import getSelectedFolderView from '../selectors/getSelectedFolderView';
import getSelectedFolderViewState from '../selectors/getSelectedFolderViewState';
import ListOrDropHint from './ListOrDropHint';
import QuickAdd from './QuickAdd';
import RecentlyCompletedTasks from './RecentlyCompletedTasks';
import TaskCreateDroppable from './TaskCreateDroppable';
import TodoAppLink from './TodoAppLink';
import TodoContextMenu from './TodoContextMenu';
import TodoList from './TodoList';
import { isFeatureEnabled } from 'owa-feature-flags';
import { loadPanel, panelClosed } from '../actions/todoPanelStoreActions';
import { TodoPanelSource } from '../store/schema/TodoPanelSource';
import { observer } from 'mobx-react';
import { KeyCodes } from '@uifabric/utilities';

const styles = require('./TodoPanel.scss');
const classNames = require('classnames');

interface TodoPanelStyles {
    rootClassName?: string;
    folderPickerClassName?: string;
    folderPickerStyles: FolderPickerStyles;
}

export interface TodoPanelProps {
    /**
     * If set to false, the folder picker won't be displayed in the panel contents.
     */
    hideFolderPicker?: boolean;
    source: TodoPanelSource;
    parentSessionId?: string;
    closePanel?: () => void;
    styles?: TodoPanelStyles;
}

@observer
export default class TodosPanel extends React.Component<TodoPanelProps, {}> {
    componentDidMount() {
        loadPanel(this.props.source, this.props.parentSessionId);
    }

    componentWillUnmount() {
        panelClosed();
    }

    render() {
        const folderView = getSelectedFolderView();
        const folderViewState = getSelectedFolderViewState();
        const contextMenuState = folderViewState ? folderViewState.contextMenuViewState : null;
        const canShowFolderPicker = !this.props.hideFolderPicker;
        const canAddTasks = canCreateTaskInFolder(folderView ? folderView.folderId : null);

        /**
         * We add the droppable on the largest region possible to minimize the area where
         * we lose track of the email being dragged from the list view over to the panel.
         */
        return (
            <div
                className={classNames(
                    styles.keyDownContainer,
                    this.props.styles && this.props.styles.rootClassName
                )}
                onKeyDown={this.onKeyDown}>
                <TaskCreateDroppable
                    classNames={classNames(
                        styles.container,
                        isFeatureEnabled('fwk-o365shellheader') &&
                            !isFeatureEnabled('cal-time-panel') &&
                            'shellDarkMode'
                    )}>
                    {canShowFolderPicker && (
                        <div
                            className={classNames(
                                styles.folderPicker,
                                this.props.styles && this.props.styles.folderPickerClassName
                            )}>
                            <FolderPicker
                                styles={{
                                    // first mix in default style overrides
                                    ...{ commandButtonTextLabelClassName: styles.folderPickerText },
                                    // then mix in prop style overrides
                                    ...(this.props.styles && this.props.styles.folderPickerStyles),
                                }}
                            />
                        </div>
                    )}

                    <ListOrDropHint>
                        <FRE />
                        {canAddTasks && <QuickAdd />}
                        <div className={styles.listContent}>
                            {folderView && <TodoList todoIds={folderView.activeTaskIds} />}
                            {folderView && <DragToEndOfList />}
                            {folderView && <RecentlyCompletedTasks />}
                        </div>
                        {contextMenuState && <TodoContextMenu />}
                        <EmptySpacer />
                    </ListOrDropHint>

                    <TodoAppLink />
                </TaskCreateDroppable>
            </div>
        );
    }

    private onKeyDown = (target: React.KeyboardEvent<unknown>) => {
        switch (target.keyCode) {
            case KeyCodes.escape:
                // If escape handling got to here, close the panel
                this.props.closePanel && this.props.closePanel();
                break;
        }
    };
}
